import * as React from 'react';
import { Link, matchPath } from 'react-router-dom';
import { first, nth, some, startsWith } from 'es-toolkit/compat';
import clsx from 'clsx';
import queryString from '@tock/query-string';

import { isValidHref } from '@tock/shared/utils/hrefValidate';

import { IApplicationContext, withApplicationContext } from 'providers/withApplicationContext';
import { isExternalNonTockUrl, pathNameFromString, wwwRoutesConfig } from 'utils/domainUtils';

import BaseComponent from '@tock/shared/components/BaseComponent';

export interface Props extends React.ComponentPropsWithoutRef<'a'> {
  'data-testid'?: string;
  innerRef?: React.Ref<HTMLAnchorElement>;
  reload?: boolean;
  trackingCategory?: string;
  allowDataProtocolUrl?: boolean;
}

interface State {
  href?: string;
  target?: string;
  processedHref?: string;
  isChangingContext: boolean;
}

const nativeProtocol = ['tel:', 'sms:', 'mailto:'];
const httpPattern = /.*:.*/;

function getIsChangingContext(props: Props & IApplicationContext): boolean {
  const { app, href } = props;
  const config = app?.config;
  const isConsumer = config?.domain?.context === 'CONSUMER';
  if (isConsumer) {
    const pathFromHref = href ? first(href.split('?')) : '';
    const hrefBusinessDomain = pathFromHref ? nth(pathFromHref.split('/'), 1) : '';
    return hrefBusinessDomain !== config?.business?.domainName;
  }

  if (href === '/' || !href) {
    return false;
  }

  const pathName = pathNameFromString(href);

  return !wwwRoutesConfig.some((config) => matchPath(pathName, config));
}

/**
 * Note: though this functionality is barely used, there is built-in analytics in this component.
 * It has never been updated to go through amplitude instead, and honestly ought to be cleaned
 * up. We did clean this out of the component in dashboard.
 * In blake-tock's words (from 8/2023), this feature dates back to when Tock didn't really know
 * how to do analytics.
 */
class Anchor extends BaseComponent<Props & IApplicationContext, State> {
  disableAnchor = false;
  static getDerivedStateFromProps(nextProps: Props & IApplicationContext, prevState: State): State {
    if (prevState && prevState.href === nextProps.href) {
      return prevState;
    }
    const { href } = nextProps;
    let processedHref = href;

    if (href?.startsWith('mailto:')) {
      if (
        href.toLocaleLowerCase().includes('?subject') ||
        href.toLocaleLowerCase().includes('?body')
      ) {
        const head = href.substring(0, href.indexOf('?') + 1);
        // Putting the tail text through queryString.parse() followed by queryString.stringify() ensures
        // that the individual values here are url-encoded, but not the key=value equal signs themselves.
        const pairs = queryString.parse(href.substring(href.indexOf('?') + 1));
        const tail = queryString.stringify(pairs);
        processedHref = `${head}${tail}`;
      }
    }

    const externalNonTockUrl = isExternalNonTockUrl(href || '');
    const isNativeLinks = some(nativeProtocol, (prefix) => startsWith(href, prefix));

    return {
      ...prevState,
      isChangingContext: getIsChangingContext(nextProps),
      target: externalNonTockUrl || isNativeLinks ? '_blank' : nextProps.target,
      href: nextProps.href,
      processedHref,
    };
  }

  constructor(props: Props & IApplicationContext) {
    super(props);
    this.state = Anchor.getDerivedStateFromProps(props, {
      isChangingContext: false,
    });
  }

  track(isOutbound: boolean) {
    if (this.props.trackingCategory && window.analytics) {
      window.analytics.sendEvent(
        this.props.trackingCategory,
        isOutbound ? 'click-outbound' : 'click',
        this.props.href,
        {
          transport: 'beacon',
        }
      );
    }
  }

  handleClickInternalUrl(event: React.MouseEvent<HTMLAnchorElement>) {
    const { onClick } = this.props;

    if (onClick) {
      onClick(event);
    }
  }

  // If we are opening the link in the same tab, stop the browser from actually
  // going to the new page until we've sent the tracking ping, and then fake the
  // effect of an anchor tag by directly setting document.location. This prevents a
  // race condition where the browser tears down the website's state (including
  // this javascript) before the outbound link ping is sent.
  handleClickExternalUrl(event: React.MouseEvent<HTMLAnchorElement>) {
    const { onClick } = this.props;

    if (onClick) {
      onClick(event);
    }

    const { processedHref: href, target } = this.state;
    if (
      !BROWSER ||
      !href ||
      href === '#' ||
      some(nativeProtocol, (prefix) => startsWith(href, prefix))
    ) {
      return;
    }

    // If the browser is opening in a new tab anyway, there's no need to fake anything.
    const isOpeningInNewTab = target === '_blank';
    const isOutbound = httpPattern.test(href);

    this.track(isOutbound);

    // Opening in New Tab.
    if (isOpeningInNewTab) {
      return;
    }

    // Opening in same tab.
    if (this.state.isChangingContext) {
      if (this.disableAnchor) {
        return;
      } else {
        this.disableAnchor = true;
      }
    }
  }

  render() {
    const { app, children, trackingCategory, reload, innerRef, ...rest } = this.props;

    const { processedHref, target } = this.state;
    const isOpeningInNewTab = target === '_blank';
    const isOutbound = httpPattern.test(rest.href || '');

    const shouldChangeContext = !app.fastRoutingBetweenContext && this.state.isChangingContext;

    if (processedHref && !isOutbound && !isOpeningInNewTab && !shouldChangeContext && !reload) {
      const props = {
        ...rest,
        className: clsx(rest.className, 'Anchor'),
        to: processedHref,
      };
      delete props.href;
      return (
        <Link ref={innerRef} {...props} onClick={this.handleClickInternalUrl}>
          {children}
        </Link>
      );
    }

    const props = {
      ...rest,
      target,
      // prevent XSS attacks
      href: isValidHref(processedHref, { allowDataProtocolUrl: this.props.allowDataProtocolUrl })
        ? processedHref
        : '',
      className: clsx(rest.className, 'Anchor'),
      rel: target === '_blank' ? 'noopener' : undefined,
    };
    return (
      <a ref={innerRef} {...props} onClick={this.handleClickExternalUrl}>
        {children}
      </a>
    );
  }
}

const AnchorWithContext = withApplicationContext(Anchor);

export default React.forwardRef<HTMLAnchorElement, Props>((props, ref) => (
  <AnchorWithContext innerRef={ref} {...props} />
));
